import { BenefitClaimModal } from '@/components/modal/BenefitClaimModal'
import { ROUTE_MY_BENEFITS } from '@/constants/Routes'
import { clubTargetUrn, useMyBenefits } from '@/services/myBenefits'
import { claimTarget, claimableBenefits } from '../claimOptions'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

/**
 * The club picker, as a route of its own.
 *
 * A route rather than state on the benefits page because the thing that opens it usually isn't the
 * benefits page: a superwall placement, a push, or the grant email all need one URL they can send
 * someone to, and a modal reachable only by tapping through a list cannot be linked.
 *
 * `grantId` is optional. Superwall fires a placement, not a grant id — it knows the member has
 * *something* claimable because that is what the targeting rule matched on, but not which row it
 * was. With no id we claim the oldest available benefit, which is the one closest to expiring and
 * so the one a member would pick anyway.
 *
 * The selection lives in the URL rather than in state. A member deciding between targets will open
 * one to read about it, and browser Back has to bring them here with their choice intact —
 * component state would be gone by then, and losing a selection to a perfectly reasonable detour is
 * how someone ends up claiming in the wrong place.
 *
 * It is carried as a target URN because a benefit can now be claimed at a club or at a coach, and
 * the URN is the one identifier both kinds share. `?club=` is still read, because it is what this
 * route wrote before coaches existed and a member may have a tab open from then.
 */
export const ClaimBenefit = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { grantId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: benefits, isLoading } = useMyBenefits()

  const legacyClubId = Number(searchParams.get('club')) || null
  const selectedTargetUrn = searchParams.get('target') ?? (legacyClubId ? clubTargetUrn(legacyClubId) : null)
  const selectTarget = (urn: string | null) => {
    // The legacy parameter is dropped on the first change either way: leaving it behind would make
    // the URL name two selections, and the reader above prefers `target`, so the stale one would
    // sit there looking authoritative and doing nothing.
    searchParams.delete('club')
    if (urn === null) {
      searchParams.delete('target')
    } else {
      searchParams.set('target', urn)
    }
    // Replace so choosing between targets does not fill history with one entry per tap — Back
    // should leave the picker, not walk back through the selections made inside it.
    setSearchParams(searchParams, { replace: true })
  }

  // Only what can be claimed here: a benefit with nowhere in the app to spend it is not offered, and
  // a link naming a bundle's part opens its bundle, whose tabs include that part.
  const benefit = grantId ? claimTarget(benefits ?? [], Number(grantId)) : claimableBenefits(benefits ?? [])[0]

  useEffect(() => {
    if (!isLoading && !benefit) navigate(ROUTE_MY_BENEFITS, { replace: true })
  }, [isLoading, benefit, navigate])

  // Closing returns to the benefits list rather than going back: arriving here from a paywall means
  // there is no useful history entry behind us, and `back` would reopen the paywall.
  const close = () => navigate(ROUTE_MY_BENEFITS, { replace: true })

  if (isLoading) {
    return <div className="py-16 text-center text-sm text-gray-400">{t('benefits.loading')}</div>
  }

  // Nothing claimable: a stale link, or they already claimed it elsewhere. The list explains the
  // state better than an error would.
  //
  // Redirected from an effect, not from the render body. Navigating during render is a side effect
  // React may run twice or discard, and this path became hot the moment the legacy `?claim=` links
  // started forwarding here.
  if (!benefit) return null

  return (
    <BenefitClaimModal
      open
      onClose={close}
      benefit={benefit}
      // No fixed target: this route exists precisely to let the member choose one.
      fixedTarget={null}
      // Closing already returns to the benefits list, which reloads and shows the claim.
      onClaimed={null}
      selectedTargetUrn={selectedTargetUrn}
      onSelectTarget={selectTarget}
    />
  )
}
